import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiSearch, FiUserX, FiUserCheck, FiEye, FiEdit, FiTrash2, FiDownload, FiAlertTriangle, FiLogOut } from 'react-icons/fi';
import { orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
  BulkActionBar,
} from '../../components/UI';
import { useFirestoreCollection, usePagination } from '../../hooks';
import {
  getUserById, banUser, unbanUser,
  deleteUserAccount, exportUserData, eraseUserData, forceLogoutUser,
  setProcessingRestriction,
} from '../../services/adminService';
import EditEntityModal from '../../components/admin/EditEntityModal';
import styles from './Users.module.css';

const statusOptions = [
  { value: 'all', label: 'All Users' },
  { value: 'active', label: 'Active' },
  { value: 'banned', label: 'Banned' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

// The app writes `name` / `middleName` / `surName` / `username` (see
// createUserProfile). This helper previously read displayName/firstName/
// lastName — fields nothing in the app ever sets — so EVERY user fell through
// to the email branch and the list showed an address where a name belongs.
// That is why Apple Hide-My-Email users appeared as a raw
// @privaterelay.appleid.com alias twice over. Legacy fields are kept as a
// fallback in case any old document still carries them.
const userFullName = (u) =>
  [u.name, u.middleName, u.surName].filter(Boolean).join(' ').trim() ||
  u.displayName ||
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
  '';

const userDisplayName = (u) =>
  userFullName(u) || (u.username ? `@${String(u.username).replace(/^@/, '')}` : '') || u.email || 'Unnamed';

/** `@handle` for display, tolerating documents that already stored the @. */
const userHandle = (u) => (u.username ? `@${String(u.username).replace(/^@/, '')}` : '');

// Grouped key/value rendering for the detail modal. A section renders nothing
// when every field in it is empty, so a personal-mode user is not padded out
// with an empty Subscription block.
const hasValue = (value) =>
  value === 0 || (value !== undefined && value !== null && value !== '' && value !== '-');

const Field = ({ label, value }) =>
  hasValue(value) ? (
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{String(value)}</span>
    </div>
  ) : null;

const Section = ({ title, children }) => {
  // Drop the whole section when nothing in it has a value, so a personal-mode
  // user is not padded out with an empty Subscription block.
  const populated = React.Children.toArray(children)
    .some((child) => React.isValidElement(child) && hasValue(child.props.value));
  if (!populated) return null;
  return (
    <div className={styles.detailSection}>
      <h4 className={styles.detailSectionTitle}>{title}</h4>
      <div className={styles.detailGrid}>{children}</div>
    </div>
  );
};

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '-');
const orDash = (v) => (v === 0 ? '0' : v || '-');

const Users = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  // Live: load latest 500 users sorted by createdAt — filter / search client-side.
  const constraints = useMemo(
    () => [orderBy('createdAt', 'desc'), limit(500)],
    []
  );
  const { data: rawUsers, loading } = useFirestoreCollection('users', constraints);

  useEffect(() => {
    if (statusFilter !== 'all') setSearchParams({ status: statusFilter });
    else setSearchParams({});
  }, [statusFilter, setSearchParams]);

  const filteredUsers = useMemo(() => {
    let list = rawUsers;
    if (statusFilter === 'banned') list = list.filter((u) => u.isBanned === true);
    else if (statusFilter === 'active') list = list.filter((u) => !u.isBanned);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      // Searchable across everything an admin would actually type: the real
      // name parts, the handle, the email, and the uid.
      list = list.filter((u) =>
        (u.email || '').toLowerCase().includes(q) ||
        userFullName(u).toLowerCase().includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        (u.id || '').toLowerCase().includes(q) ||
        (u.displayName || '').toLowerCase().includes(q) ||
        (u.firstName || '').toLowerCase().includes(q) ||
        (u.lastName || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [rawUsers, statusFilter, searchQuery]);

  const {
    page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize,
  } = usePagination(filteredUsers, 25);

  const openUserDetail = async (user) => {
    setErrorMsg('');
    try {
      const fullUser = await getUserById(user.id);
      setSelectedUser(fullUser);
      setShowDetailModal(true);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Failed to load user: ${err.message}`);
    }
  };

  const handleBanUser = async () => {
    if (!selectedUser || !banReason) return;
    try {
      setActionLoading(true);
      await banUser(selectedUser.id, banReason);
      setShowBanModal(false);
      setBanReason('');
      setSelectedUser(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Ban failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceLogout = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Force log out ${selectedUser.email}? They'll be signed out within an hour.`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await forceLogoutUser({ targetUserId: selectedUser.id });
    } catch (err) {
      setErrorMsg(`Force-logout failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedUser) return;
    if (!window.confirm(
      `HARD DELETE the account ${selectedUser.email}? This removes their Auth user and profile doc. Use this for spam/fraud — for GDPR right-to-erasure use Erase data instead. This is irreversible.`
    )) return;
    const reason = window.prompt('Optional reason (audit log):');
    if (reason === null) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await deleteUserAccount({ targetUserId: selectedUser.id, reason: reason || undefined });
      setShowDetailModal(false);
      setSelectedUser(null);
    } catch (err) {
      setErrorMsg(`Delete failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportData = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      const result = await exportUserData({ targetUserId: selectedUser.id });
      // Offer the bundle as a downloadable JSON file.
      const json = JSON.stringify(result, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-${selectedUser.id}-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMsg(`Export failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestrictProcessing = async () => {
    if (!selectedUser) return;
    const reason = window.prompt(
      'Reason for restriction (audit log) — typically a GDPR ticket reference or note about the dispute:'
    );
    if (reason === null) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await setProcessingRestriction({
        targetUserId: selectedUser.id,
        restricted: true,
        reason: reason || 'GDPR Art. 18 restriction',
      });
      const fresh = await getUserById(selectedUser.id);
      if (fresh) setSelectedUser(fresh);
    } catch (err) {
      setErrorMsg(`Restrict failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnrestrictProcessing = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Lift the Art. 18 restriction on ${selectedUser.email}?`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await setProcessingRestriction({
        targetUserId: selectedUser.id,
        restricted: false,
      });
      const fresh = await getUserById(selectedUser.id);
      if (fresh) setSelectedUser(fresh);
    } catch (err) {
      setErrorMsg(`Lift failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEraseData = async () => {
    if (!selectedUser) return;
    if (!window.confirm(
      `GDPR RIGHT-TO-ERASURE for ${selectedUser.email}. This anonymizes their content (posts/comments → "[deleted]") and removes their Auth user + profile. Cannot be undone. Continue?`
    )) return;
    const reason = window.prompt('Reason (audit log) — typically a GDPR ticket reference:');
    if (reason === null) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      const result = await eraseUserData({ targetUserId: selectedUser.id, reason: reason || 'GDPR right-to-erasure' });
      alert(`Erased. ${result?.counts ? JSON.stringify(result.counts) : ''}`);
      setShowDetailModal(false);
      setSelectedUser(null);
    } catch (err) {
      setErrorMsg(`Erase failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnbanUser = async (user) => {
    try {
      setActionLoading(true);
      await unbanUser(user.id);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Unban failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
  };

  const toggleAllOnPage = (checked) => {
    const ids = pageData.map((u) => u.id);
    setSelectedIds((prev) => {
      if (checked) return Array.from(new Set([...prev, ...ids]));
      const dropSet = new Set(ids);
      return prev.filter((x) => !dropSet.has(x));
    });
  };

  const clearSelection = () => setSelectedIds([]);

  const bulkBan = async () => {
    const reason = window.prompt('Reason for banning the selected users:');
    if (!reason) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => banUser(id, reason)));
      clearSelection();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Bulk ban failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const bulkUnban = async () => {
    if (!window.confirm(`Unban ${selectedIds.length} users?`)) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => unbanUser(id)));
      clearSelection();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Bulk unban failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const csvColumns = [
    { key: 'id', label: 'User ID' },
    { key: 'email', label: 'Email' },
    { key: 'name', label: 'First name' },
    { key: 'middleName', label: 'Middle name' },
    { key: 'surName', label: 'Surname' },
    { key: 'username', label: 'Username' },
    { key: 'authProvider', label: 'Sign-in method' },
    { key: 'isPrivacySet', label: 'Onboarded', accessor: (u) => (u.isPrivacySet === true ? 'yes' : 'no') },
    { key: 'countryCode', label: 'Country' },
    { key: 'subscriptionTier', label: 'Tier' },
    { key: 'isBanned', label: 'Banned', accessor: (u) => (u.isBanned ? 'yes' : 'no') },
    { key: 'banReason', label: 'Ban Reason' },
    { key: 'isAdmin', label: 'Admin', accessor: (u) => (u.isAdmin ? 'yes' : 'no') },
    { key: 'createdAt', label: 'Joined', accessor: (u) => formatDate(u.createdAt) },
    { key: 'lastSeen', label: 'Last Seen', accessor: (u) => formatDate(u.lastSeen) },
  ];

  const columns = [
    {
      key: 'displayName',
      title: 'User',
      render: (_, row) => (
        <div className={styles.userCell}>
          <div className={styles.avatar}>
            {(row.avatar || row.photoURL) ? (
              <img src={(row.avatar || row.photoURL)} alt="" />
            ) : (
              <span>{userDisplayName(row).charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className={styles.userInfo}>
            <span className={styles.userName}>{userDisplayName(row)}</span>
            <span className={styles.userEmail}>
              {[userHandle(row), row.email].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      title: 'Role',
      render: (_, row) => {
        if (row.isAdmin) return <Badge variant="primary">Admin</Badge>;
        if (row.vet?.isVerified) return <Badge variant="secondary">Vet</Badge>;
        if (row.businessProfile?.isVerified) return <Badge variant="secondary">Business</Badge>;
        return <Badge variant="inactive">User</Badge>;
      },
    },
    {
      key: 'status',
      title: 'Status',
      render: (_, row) =>
        row.isBanned ? (
          <Badge variant="danger">banned</Badge>
        ) : (
          <Badge variant="success">active</Badge>
        ),
    },
    {
      key: 'createdAt',
      title: 'Joined',
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      title: '',
      width: '120px',
      render: (_, row) => (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            icon={<FiEye />}
            onClick={() => openUserDetail(row)}
          />
          {row.isBanned ? (
            <Button
              variant="ghost"
              size="sm"
              icon={<FiUserCheck />}
              onClick={() => handleUnbanUser(row)}
              disabled={actionLoading}
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              icon={<FiUserX />}
              onClick={() => {
                setSelectedUser(row);
                setShowBanModal(true);
              }}
              disabled={actionLoading}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Users</h1>
          {!loading && <Badge variant="success">{rawUsers.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          Manage user accounts and permissions
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
          <div className={styles.filters}>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${statusFilter === option.value ? styles.active : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton
            rows={filteredUsers}
            columns={csvColumns}
            filename="dierend-users"
          />
        </div>

        <BulkActionBar selectedCount={selectedIds.length} onClear={clearSelection}>
          <Button variant="danger" size="sm" onClick={bulkBan} disabled={actionLoading}>
            Ban Selected
          </Button>
          <Button variant="primary" size="sm" onClick={bulkUnban} disabled={actionLoading}>
            Unban Selected
          </Button>
        </BulkActionBar>

        <Table
          columns={columns}
          data={pageData}
          loading={loading}
          emptyMessage="No users found"
          selectable
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAllOnPage}
        />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="User Details"
        size="lg"
        footer={selectedUser && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)}>Close</Button>
            <Button variant="ghost" size="sm" icon={<FiEdit />} onClick={() => setShowEditModal(true)}>Edit profile</Button>
            <Button variant="ghost" size="sm" icon={<FiDownload />} onClick={handleExportData} loading={actionLoading} title="GDPR right-to-export — downloads JSON bundle">
              Export data
            </Button>
            <Button variant="ghost" size="sm" icon={<FiLogOut />} onClick={handleForceLogout} loading={actionLoading} title="Revoke refresh tokens; user signs out within 1h">
              Force logout
            </Button>
            {!selectedUser.isBanned ? (
              <Button variant="danger" size="sm" icon={<FiUserX />} onClick={() => { setShowDetailModal(false); setShowBanModal(true); }}>
                Ban
              </Button>
            ) : (
              <Button variant="primary" size="sm" icon={<FiUserCheck />} onClick={() => handleUnbanUser(selectedUser)} loading={actionLoading}>
                Unban
              </Button>
            )}
            <Button variant="danger" size="sm" icon={<FiAlertTriangle />} onClick={handleEraseData} loading={actionLoading} title="GDPR right-to-erasure — anonymizes content + deletes account">
              Erase (GDPR)
            </Button>
            <Button variant="danger" size="sm" icon={<FiTrash2 />} onClick={handleDeleteAccount} loading={actionLoading} title="Hard-delete for spam/fraud; not for GDPR">
              Delete account
            </Button>
          </div>
        )}
      >
        {selectedUser && (
          <div className={styles.detailContent}>
            <div className={styles.detailHeader}>
              <div className={styles.avatarLarge}>
                {(selectedUser.avatar || selectedUser.photoURL) ? (
                  <img src={selectedUser.avatar || selectedUser.photoURL} alt="" />
                ) : (
                  <span>{userDisplayName(selectedUser).charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div>
                <h3>{userDisplayName(selectedUser)}</h3>
                <p>{[userHandle(selectedUser), selectedUser.email].filter(Boolean).join(' · ')}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {selectedUser.isBanned ? (
                    <Badge variant="danger">banned</Badge>
                  ) : (
                    <Badge variant="success">active</Badge>
                  )}
                  {selectedUser.authProvider && (
                    <Badge variant="secondary">{selectedUser.authProvider}</Badge>
                  )}
                  {/* Onboarding gate is `isPrivacySet !== true` — anything other
                      than true means the user never finished onboarding and is
                      sitting in the flow, which is invisible from the list. */}
                  {selectedUser.isPrivacySet !== true && (
                    <Badge variant="warning">onboarding incomplete</Badge>
                  )}
                  {selectedUser.isAdmin && <Badge variant="primary">admin</Badge>}
                </div>
              </div>
            </div>

            <Section title="Identity">
              <Field label="First name" value={selectedUser.name} />
              <Field label="Middle name" value={selectedUser.middleName} />
              <Field label="Surname" value={selectedUser.surName} />
              <Field label="Username" value={userHandle(selectedUser)} />
              <Field label="Date of birth" value={selectedUser.dob} />
              <Field label="Gender" value={selectedUser.gender} />
              <Field label="Bio" value={selectedUser.bio} />
              <Field label="Pet name" value={selectedUser.petName} />
            </Section>

            <Section title="Account">
              <Field label="User ID" value={selectedUser.id} />
              <Field label="Email" value={selectedUser.email} />
              <Field label="Sign-in method" value={selectedUser.authProvider} />
              <Field label="Account type" value={selectedUser.accountType} />
              <Field label="Onboarding complete" value={yesNo(selectedUser.isPrivacySet === true)} />
              <Field label="Joined" value={formatDate(selectedUser.createdAt)} />
              <Field label="Last updated" value={formatDate(selectedUser.updatedAt)} />
              <Field label="Last seen" value={formatDate(selectedUser.lastSeen)} />
              <Field label="Country" value={selectedUser.countryCode} />
              <Field label="Language" value={selectedUser.language} />
              <Field label="Acquisition" value={selectedUser.acquisitionSource} />
              <Field label="Phone" value={selectedUser.phoneNumber} />
            </Section>

            <Section title="Activity">
              <Field label="Followers" value={orDash(selectedUser.followersCount)} />
              <Field label="Following" value={orDash(selectedUser.followingCount)} />
              <Field label="Pets" value={orDash(selectedUser.petsCount)} />
              <Field label="Reports against user" value={orDash(selectedUser.reportsCount)} />
            </Section>

            <Section title="Subscription">
              <Field label="Tier" value={selectedUser.subscriptionTier} />
              <Field label="Status" value={selectedUser.subscriptionStatus} />
              <Field label="Billing cycle" value={selectedUser.subscriptionBillingCycle} />
              <Field label="Store" value={selectedUser.subscriptionPlatform} />
              <Field label="Started" value={formatDate(selectedUser.subscriptionStartDate)} />
              <Field label="Ends / renews" value={formatDate(selectedUser.subscriptionEndDate)} />
              <Field label="Auto-renew" value={yesNo(selectedUser.subscriptionAutoRenew)} />
              <Field label="Product ID" value={selectedUser.subscriptionProductId} />
            </Section>

            <Section title="Device & notifications">
              <Field label="Platform" value={selectedUser.devicePlatform} />
              <Field label="OS" value={selectedUser.deviceOS} />
              <Field label="Notifications" value={yesNo(selectedUser.notificationsEnabled)} />
              <Field label="Push token" value={selectedUser.pushToken ? 'registered' : 'none'} />
              <Field label="Token updated" value={formatDate(selectedUser.pushTokenUpdatedAt)} />
              <Field label="Online now" value={yesNo(selectedUser.isOnline)} />
            </Section>

            <Section title="Flags">
              <Field label="Admin" value={yesNo(selectedUser.isAdmin === true)} />
              <Field label="Private profile" value={yesNo(selectedUser.isPrivate)} />
              <Field label="Banned" value={yesNo(selectedUser.isBanned === true)} />
              <Field label="Under 18" value={yesNo(selectedUser.isUnder18)} />
              <Field label="Analytics consent" value={yesNo(selectedUser.analyticsConsent)} />
              <Field label="Business pending" value={yesNo(selectedUser.pendingBusinessRegistration)} />
              <Field label="Vet pending" value={yesNo(selectedUser.pendingVetRegistration)} />
              <Field label="Parent email" value={selectedUser.parentEmail} />
            </Section>

            {selectedUser.banReason && (
              <div className={styles.banInfo}>
                <h4>Ban Reason</h4>
                <p>{selectedUser.banReason}</p>
              </div>
            )}

            {/* GDPR / DSA compliance pane — read-only consent log + admin
                toggle for Art. 18 restriction of processing. */}
            <div style={{ marginTop: 24, padding: 16, background: '#0f1218', borderRadius: 8, border: '1px solid #2a2f3a' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#e8eaed', fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Compliance &amp; consent
              </h4>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Privacy policy</span>
                  <span className={styles.detailValue}>
                    {selectedUser.privacyPolicyVersion
                      ? `v${selectedUser.privacyPolicyVersion} on ${formatDate(selectedUser.privacyPolicyAcceptedAt)}`
                      : (selectedUser.isPrivacySet ? 'accepted (no version)' : 'not accepted')}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Terms &amp; conditions</span>
                  <span className={styles.detailValue}>
                    {selectedUser.termsVersion
                      ? `v${selectedUser.termsVersion} on ${formatDate(selectedUser.termsAcceptedAt)}`
                      : 'not recorded'}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Marketing opt-in</span>
                  <span className={styles.detailValue}>
                    {selectedUser.marketingOptIn === false ? 'OPTED OUT' : (selectedUser.marketingOptIn === true ? 'opted in' : 'not set')}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Cookie consent</span>
                  <span className={styles.detailValue}>{selectedUser.cookieConsent || 'not recorded'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Parental approval</span>
                  <span className={styles.detailValue}>
                    {selectedUser.parentalApproved === true ? 'approved'
                      : selectedUser.parentalRejected === true ? 'rejected'
                      : (selectedUser.requiresParentalApproval ? 'pending' : 'n/a')}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Processing restricted (Art. 18)</span>
                  <span className={styles.detailValue}>
                    {selectedUser.processingRestricted ? 'YES' : 'no'}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                {selectedUser.processingRestricted ? (
                  <Button size="sm" variant="primary" onClick={handleUnrestrictProcessing} loading={actionLoading}>
                    Lift Art. 18 restriction
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={handleRestrictProcessing} loading={actionLoading} title="GDPR Art. 18 — restrict processing while a complaint or rectification request is being investigated">
                    Restrict processing (Art. 18)
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showBanModal}
        onClose={() => {
          setShowBanModal(false);
          setBanReason('');
          setSelectedUser(null);
        }}
        title="Ban User"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowBanModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleBanUser} loading={actionLoading} disabled={!banReason}>
              Ban User
            </Button>
          </>
        }
      >
        <p className={styles.banText}>
          You are about to ban <strong>{userDisplayName(selectedUser || {})}</strong>.
          Please provide a reason:
        </p>
        <Input
          placeholder="Enter ban reason..."
          value={banReason}
          onChange={(e) => setBanReason(e.target.value)}
        />
      </Modal>

      <EditEntityModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        entityType="user"
        entity={selectedUser}
        onSaved={() => {
          if (selectedUser?.id) {
            getUserById(selectedUser.id).then((u) => u && setSelectedUser(u));
          }
        }}
      />
    </div>
  );
};

export default Users;
